/**
 * Docs
 *
 * User-facing how-to instructions for CRM features. Static content in code
 * for v1 -- see project-brain/pages/docs.md.
 */

import React from 'react';
import Card from '@components/ui/Card';
import './Docs.css';

const Docs: React.FC = () => {
  return (
    <div className="docs-page">
      <h1 className="docs-title">Docs</h1>

      <Card className="docs-article">
        <h2 className="section-title">Connecting a Project to Google Chat</h2>

        <p>
          BBH-Bot lets anyone in a linked Google Chat space ask for a project's open GitHub issues, or
          add a note to one, without asking an engineer to run anything manually.
        </p>

        <h3>Available commands</h3>
        <p>
          Everything goes through one command, <code>/brain</code>, followed by what you want to do:
        </p>
        <ul>
          <li><code>/brain outcomes</code> — lists the repo's open issues, e.g. <code>#42 Review onboarding flow</code>.</li>
          <li>
            <code>/brain outcome &lt;issue number&gt;</code> — shows that issue's full description. If it's too
            long for one Chat message, BBH-Bot replies with a numbered list of its sections instead.
          </li>
          <li>
            <code>/brain section &lt;issue number&gt; &lt;section number&gt;</code> — shows just that one section,
            using the numbers from the <code>/brain outcome</code> menu.
          </li>
          <li>
            <code>/brain outcome edit &lt;issue number&gt;</code> — pick a section to edit from a button menu, then
            type the new text into a pop-up box (pre-filled with whatever's there already, so you're editing rather
            than starting from scratch). You'll get a chance to confirm before anything is written to GitHub.
          </li>
          <li>
            <code>/brain new outcome &lt;title&gt;</code> — creates a new outcome issue and walks you straight into
            filling in its Business Objective and Success Criteria, using the same pop-up-and-confirm flow as
            editing.
          </li>
          <li>
            <code>/brain note &lt;issue number&gt; &lt;text&gt;</code> — stages a comment on that issue. Nothing is
            written to GitHub until you confirm.
          </li>
          <li>
            <code>/brain confirm &lt;id&gt;</code> — the id BBH-Bot gives you after a <code>/brain note</code>.
            Confirms and actually posts the comment. Expires after 10 minutes if you don't confirm.
          </li>
          <li>
            <code>/brain cancel</code> — abandons whatever section edit or new outcome you're partway through.
          </li>
        </ul>

        <h3>Connecting a project (one-time, per project)</h3>
        <ol>
          <li>
            Open the project's detail page and click <strong>Connect Chat Integration</strong>.
          </li>
          <li>
            <strong>GitHub side:</strong> pick an already-installed repo from the list, or click{' '}
            <strong>Install on a new repo</strong> to go to GitHub's install screen. Choose{' '}
            <strong>Only select repositories</strong> there — not "All repositories" — and pick just the one repo
            you're connecting.
          </li>
          <li>
            <strong>Chat side:</strong> in Google Chat, open the space you want to use and add{' '}
            <strong>BBH-Bot</strong> to it (search for it and add, same as adding any Chat app to a space). Send
            it any message so the CRM notices the space exists.
          </li>
          <li>
            Back in the CRM, pick that space from the dropdown and click <strong>Link space</strong>.
          </li>
          <li>
            Click <strong>Test connection</strong> to confirm both sides actually work.
          </li>
        </ol>

        <h3>If BBH-Bot says a space isn't linked</h3>
        <p>
          That means someone added BBH-Bot to a Chat space but no CRM admin has connected it to a project yet.
          Follow the steps above from a project's detail page to link it.
        </p>
      </Card>
    </div>
  );
};

export default Docs;
